import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ShareDialog } from '@/components/share/ShareDialog';
import { AnonymizeStudyDialog } from '@/components/studies/AnonymizeStudyDialog';
import { DicomSendDialog } from '@/components/studies/DicomSendDialog';
import { DownloadStudyDialog } from '@/components/studies/DownloadStudyDialog';
import { EmailStudyDialog } from '@/components/studies/EmailStudyDialog';
import { MergeStudiesDialog } from '@/components/studies/MergeStudiesDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/components/ui/table';
import { DeleteModal } from '@/components/viewer/DeleteModal';
import { AnonymizeJobWatcher, useAnonymizeStudy } from '@/hooks/use-anonymize-study';
import { useStudyClinics } from '@/hooks/use-clinics';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useMediaQuery } from '@/hooks/use-media-query';
import { usePrefetchStudySummary, warmViewerEngine } from '@/hooks/use-studies';
import { useStudyWarmup } from '@/hooks/use-study-warmup';
import { toast } from '@/hooks/use-toast';
import { useViewerSettings } from '@/hooks/useViewerSettings';
import { trackStudyAction } from '@/lib/analytics/viewer';
import { getStudyList } from '@/lib/api';
import { multiScreenService } from '@/lib/multiScreen';
import { notifyPopupBlocked } from '@/lib/multiScreen/popupGuidance';
import { DEFAULT_WORKLIST_COLUMNS } from '@/lib/settings/defaults';
import { clearWorklistReturn, peekWorklistReturn, rememberWorklistReturn, setNavigationContext, } from '@/lib/studyNavigation';
import { MessageSquare } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useReceivingStudyIds } from './useReceivingStudyIds';
import { WorklistCardList } from './WorklistCardList';
import { WorklistColumnsMenu } from './WorklistColumnsMenu';
import { studyRowDisplay } from './worklistDisplay';
import { ExpandedRowContent } from './WorklistExpandedRow';
import { EXPANDER_COLUMN_WIDTH, NOTIFY_COLUMN_WIDTH, WorklistHeaderCell, WorklistTableRow, } from './WorklistTableRow';
const CUSTOMIZABLE_COLUMNS = [
    'pid',
    'patientName',
    'description',
    'dateTime',
    'modality',
    'series',
    'images',
    'accession',
    'institution',
];
/**
 * Intersects the user's visible-column preference with what fits at the
 * current viewport: the fixed column widths total ~990px (that sum is the
 * table's minimum width under table-layout: fixed; there is deliberately no
 * separate min-w class, so the full column set at xl fits the ~1007-1024px
 * content box beside the 224px sidebar without pushing NOTIFY behind a
 * scroll). Below xl the low-value accession + institution columns are
 * dropped, and below lg the series + images counts go too (~682px, which fits
 * iPad mini portrait and iPhone landscape). Below sm there is no table: see
 * WorklistCardList. Header, body, and colSpan all derive from the returned
 * list so the table never renders mismatched cells.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function intersectColumnsWithViewport(columns, viewport) {
    if (viewport.isXl)
        return columns;
    const hidden = new Set(['accession', 'institution']);
    if (!viewport.isLg) {
        hidden.add('series');
        hidden.add('images');
    }
    return columns.filter((c) => !hidden.has(c.key));
}
export function WorklistTable({ studies, total, isLoading, filters, onFiltersChange, expandStudyUid, }) {
    const { t } = useTranslation();
    const [, setLocation] = useLocation();
    // A membership-less, non-super-admin account (TC-only) cannot own studies:
    // the empty state points at the Teleconsultation worklist instead. A
    // membership-less super admin sees every clinic, so it keeps the plain
    // "No studies found".
    const { currentUser, isSuperAdmin } = useCurrentUser();
    const hasNoClinic = !!currentUser && currentUser.clinicRoles.length === 0 && !isSuperAdmin;
    const [expandedStudies, setExpandedStudies] = useState(new Set());
    // Study whose consultation the stethoscope glyph asked to open: the row
    // expands and its content pops the modal once the case has loaded.
    const [consultationToOpen, setConsultationToOpen] = useState(null);
    const [deleteModalStudyId, setDeleteModalStudyId] = useState(null);
    const [shareDialogStudyId, setShareDialogStudyId] = useState(null);
    const [emailDialogStudyId, setEmailDialogStudyId] = useState(null);
    const [downloadStudyIds, setDownloadStudyIds] = useState(null);
    const [dicomSendStudyId, setDicomSendStudyId] = useState(null);
    const [mergeDialogStudyId, setMergeDialogStudyId] = useState(null);
    const [anonymizeStudyId, setAnonymizeStudyId] = useState(null);
    // Jobs this session started; each gets a watcher that toasts and refreshes on completion.
    const [anonymizeJobIds, setAnonymizeJobIds] = useState([]);
    const anonymizeMutation = useAnonymizeStudy();
    const { settings, updateSettings } = useViewerSettings();
    const { data: clinics = [] } = useStudyClinics();
    // Prefetches the SUMMARY, because that is the payload the viewer opens on.
    // Warming ['study', id, null] here would be dead weight: the viewer reads
    // ['study-summary', id, null] and would never see it.
    const prefetchStudy = usePrefetchStudySummary();
    // Pixel warm-up rides on the same signals as the summary prefetch: an open
    // card warms the first-paint images, a closed one stops, a click hands off.
    const studyWarmup = useStudyWarmup();
    // Prefetch-on-intent: hovering a worklist row warms both the study metadata
    // (React Query cache) and the viewer's Cornerstone engine, so a subsequent
    // click opens against a warm cache. Debounced so a mouse merely sweeping
    // across rows doesn't fire dozens of prefetches — only a deliberate ~140ms
    // hover commits. Note: only small JSON metadata is fetched here, never pixel
    // data, so abandoning a hover (e.g. to open a different study) wastes at most
    // one tiny request — there's no large in-flight download to cancel.
    const rowHoverTimerRef = useRef(null);
    const handleRowHover = useCallback((id) => {
        if (rowHoverTimerRef.current)
            clearTimeout(rowHoverTimerRef.current);
        rowHoverTimerRef.current = setTimeout(() => {
            prefetchStudy(id);
            warmViewerEngine();
        }, 140);
    }, [prefetchStudy]);
    const handleRowHoverEnd = useCallback(() => {
        if (rowHoverTimerRef.current) {
            clearTimeout(rowHoverTimerRef.current);
            rowHoverTimerRef.current = null;
        }
    }, []);
    // A pending hover timer must not outlive the table (it would fire against an
    // unmounted tree — and, in tests, against torn-down mocks).
    useEffect(() => {
        return () => {
            if (rowHoverTimerRef.current)
                clearTimeout(rowHoverTimerRef.current);
        };
    }, []);
    // Warm the viewer engine once the worklist is on screen (idle time). The
    // radiologist viewing the worklist is the population about to open a study,
    // so pre-initializing the rendering engine + worker pool here removes that
    // cost from the critical path of the first viewer open. Idempotent + chunk-
    // cached, so this is a no-op after the first run.
    useEffect(() => {
        // requestIdleCallback isn't available in older Safari — fall back to a timer.
        const ric = window.requestIdleCallback;
        if (ric) {
            const handle = ric(() => warmViewerEngine(), { timeout: 3000 });
            return () => window.cancelIdleCallback?.(handle);
        }
        const handle = window.setTimeout(() => warmViewerEngine(), 1500);
        return () => clearTimeout(handle);
    }, []);
    const clinicNameById = useMemo(() => new Map(clinics.map((clinic) => [clinic.id, clinic.name])), [clinics]);
    const displayFor = (study) => studyRowDisplay(study, t('worklist.unknown'), clinicNameById.get(study.clinicId));
    // Derived from the list snapshots the worklist already polls for — no extra
    // query and no extra request. See useReceivingStudyIds.
    const { receiving: receivingStudyIds, settle: settleReceiving } = useReceivingStudyIds(studies);
    const configuredColumns = settings.studiesWorklist?.columns?.length
        ? settings.studiesWorklist.columns
        : DEFAULT_WORKLIST_COLUMNS;
    const customColumns = configuredColumns.filter((c) => c.key !== 'notify' && CUSTOMIZABLE_COLUMNS.includes(c.key));
    // What the user has toggled on (drives the columns popover)…
    const preferredVisibleColumns = customColumns.filter((c) => c.visible);
    // …intersected with what fits at the current viewport (drives the table).
    // Fallback `true` = assume desktop where matchMedia is unavailable.
    const isXl = useMediaQuery('(min-width: 1280px)', true);
    const isLg = useMediaQuery('(min-width: 1024px)', true);
    // Below sm (phones) the list renders as stacked cards: even the narrowest
    // column set is ~680px, so date, modality and status sat off-screen.
    const isSm = useMediaQuery('(min-width: 640px)', true);
    const visibleColumns = intersectColumnsWithViewport(preferredVisibleColumns, { isXl, isLg });
    const showNotifyColumn = configuredColumns.find((c) => c.key === 'notify')?.visible !== false;
    const columnCount = 1 + visibleColumns.length + (showNotifyColumn ? 1 : 0);
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 25;
    const totalPages = Math.ceil(total / pageSize);
    const handleSort = (field) => {
        if (filters.sortBy === field) {
            onFiltersChange({
                ...filters,
                sortDirection: filters.sortDirection === 'Asc' ? 'Desc' : 'Asc',
            });
        }
        else {
            onFiltersChange({
                ...filters,
                sortBy: field,
                sortDirection: 'Desc',
            });
        }
    };
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            onFiltersChange({
                ...filters,
                page: newPage,
            });
        }
    };
    const saveColumnPrefs = (nextColumns) => {
        updateSettings({
            studiesWorklist: {
                columns: nextColumns,
            },
        });
    };
    const setCustomColumnVisibility = (key, visible) => {
        if (!visible && preferredVisibleColumns.length <= 1)
            return;
        const next = configuredColumns.map((col) => (col.key === key ? { ...col, visible } : col));
        saveColumnPrefs(next);
    };
    const moveCustomColumn = (key, direction) => {
        const allCustom = configuredColumns.filter((col) => col.key !== 'notify' && CUSTOMIZABLE_COLUMNS.includes(col.key));
        const currentIndex = allCustom.findIndex((col) => col.key === key);
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= allCustom.length)
            return;
        const reordered = [...allCustom];
        const [moved] = reordered.splice(currentIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        const notifyCol = configuredColumns.find((col) => col.key === 'notify');
        const nextColumns = [
            ...reordered,
            ...(notifyCol ? [notifyCol] : [{ key: 'notify', visible: true }]),
        ];
        saveColumnPrefs(nextColumns);
    };
    const resetColumnPrefs = () => {
        saveColumnPrefs([...DEFAULT_WORKLIST_COLUMNS]);
    };
    // Latest openViewer, so the popup-blocked "Retry" closure can re-run it
    // without the callback referencing itself (use-before-define).
    const openViewerRef = useRef(() => { });
    // A physical double-click dispatches two click events; without a guard the
    // repeat invocation can re-navigate a still-booting multi-monitor viewer
    // popup and double-count analytics. Swallow repeat opens of the same target
    // within a short window.
    const lastOpenRef = useRef(null);
    const openViewer = useCallback(async (studyId, seriesId, returnCardId) => {
        const openKey = `${studyId}|${seriesId ?? ''}`;
        const now = Date.now();
        if (lastOpenRef.current &&
            lastOpenRef.current.key === openKey &&
            now - lastOpenRef.current.at < 750) {
            return;
        }
        lastOpenRef.current = { key: openKey, at: now };
        // The card's warm-up must outlive this component: the viewer wants those bytes.
        studyWarmup.handoff(studyId);
        trackStudyAction('study.opened', { withSeries: Boolean(seriesId) });
        // Populate study navigation context for prev/next in the viewer
        setNavigationContext({
            studyIds: studies.map((s) => s.id),
            total,
            currentPage: page,
            pageSize,
            fetchPage: async (p) => {
                const result = await getStudyList({ ...filters, page: p });
                return { studyIds: result.results.map((s) => s.id), total: result.total };
            },
        });
        // Multi-monitor: route onto the viewer screen. A live viewer window is
        // navigated in place over the channel (no reload, no popup); otherwise
        // ONE named window opens for the whole reading session. A blocked popup
        // surfaces the allow-popups guidance.
        const { handled, blockedLeg } = await multiScreenService.openCockpit({
            studyId,
            seriesId: seriesId ?? undefined,
            primary: 'viewer',
        });
        if (handled) {
            if (blockedLeg)
                notifyPopupBlocked(blockedLeg, () => void openViewerRef.current(studyId, seriesId, returnCardId));
            return;
        }
        // Fallback: normal navigation in same window. This unmounts the
        // worklist, so remember the launch point — the launched study's own
        // card (or, for a related-study open, the card that hosted it) plus the
        // current filter/page query string — so the return trip can restore it.
        rememberWorklistReturn({
            studyId: returnCardId ?? studyId,
            search: window.location.search,
        });
        let url = `/viewer/${encodeURIComponent(studyId)}`;
        if (seriesId) {
            url += `?series=${encodeURIComponent(seriesId)}`;
        }
        setLocation(url);
    }, [setLocation, studies, total, page, pageSize, filters, studyWarmup]);
    useEffect(() => {
        openViewerRef.current = openViewer;
    }, [openViewer]);
    const toggleExpand = (id) => {
        if (expandedStudies.has(id)) {
            // Clicking the already-open card closes it
            setExpandedStudies(new Set());
            studyWarmup.cancel(id);
        }
        else {
            // Opening a new card closes any previously open card
            setExpandedStudies(new Set([id]));
            prefetchStudy(id);
            studyWarmup.warm(id);
        }
    };
    // Deep-link from the "Recent imports" menu (?expandStudyUid=<StudyInstanceUID>):
    // once the matching study is in the loaded worklist, open its card, prefetch it,
    // scroll it into view, then strip the param so a later refetch doesn't re-open it.
    const handledExpandUidRef = useRef(null);
    useEffect(() => {
        if (!expandStudyUid) {
            handledExpandUidRef.current = null;
            return;
        }
        if (handledExpandUidRef.current === expandStudyUid)
            return;
        const match = studies.find((s) => s.studyUid === expandStudyUid);
        if (!match)
            return; // not in the current page yet; wait for the refetch/poll
        handledExpandUidRef.current = expandStudyUid;
        prefetchStudy(match.id);
        studyWarmup.warm(match.id);
        // Drop the deep-link param but keep the rest of the worklist URL state
        // (search/sort/page must survive the strip).
        const params = new URLSearchParams(window.location.search);
        params.delete('expandStudyUid');
        const remaining = params.toString();
        setLocation(remaining ? `/study/list?${remaining}` : '/study/list', { replace: true });
        // Defer the open + scroll out of the effect body (avoids a synchronous
        // setState-in-effect) and gives the row a frame to be present in the DOM.
        // Deliberately NOT cancelled on cleanup: the setLocation above re-runs this
        // effect (expandStudyUid → undefined) before the frame fires, so a cleanup
        // cancel would guarantee the card never opens.
        requestAnimationFrame(() => {
            setExpandedStudies(new Set([match.id]));
            document
                .querySelector(`[data-testid="study-row-${match.id}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [expandStudyUid, studies, prefetchStudy, studyWarmup, setLocation]);
    // Restore the card the user launched from when returning from the viewer
    // (browser Back or the viewer's "Study List" button). One-shot: the first
    // settled study list decides and the stored marker is cleared in the same
    // stroke — if the launched study isn't in it (filters or page changed
    // since), the restore is dropped rather than left to pop a card open on a
    // later page. The marker stays in sessionStorage until then, so a transient
    // fetch error that unmounts the table doesn't destroy it before it could
    // apply.
    useEffect(() => {
        if (isLoading)
            return;
        const marker = peekWorklistReturn();
        if (!marker)
            return;
        clearWorklistReturn();
        // An explicit ?expandStudyUid= deep link outranks the return marker.
        if (expandStudyUid)
            return;
        const match = studies.find((s) => s.id === marker.studyId);
        if (!match)
            return;
        // Same deferral as the deep-link effect above: give the row a frame to
        // exist in the DOM before expanding and scrolling to it. The marker is
        // already consumed above, so if the effect re-runs before the frame
        // fires, the cleanup cancels the stale frame and the restore is simply
        // dropped rather than firing against an unmounted or stale table.
        const raf = requestAnimationFrame(() => {
            setExpandedStudies(new Set([match.id]));
            document
                .querySelector(`[data-testid="study-row-${match.id}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return () => cancelAnimationFrame(raf);
    }, [isLoading, studies, expandStudyUid]);
    const handleRowClick = (e, id) => {
        // Don't toggle if clicking on interactive elements
        const target = e.target;
        if (target.closest('button') ||
            target.closest('input') ||
            target.closest('[role="checkbox"]')) {
            return;
        }
        toggleExpand(id);
    };
    // Drag handlers for study rows
    const [draggingStudyId, setDraggingStudyId] = useState(null);
    const handleDragStart = useCallback((e, studyId) => {
        setDraggingStudyId(studyId);
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('application/x-study-id', studyId);
        // Also set text for accessibility
        e.dataTransfer.setData('text/plain', studyId);
    }, []);
    const handleDragEnd = useCallback(() => {
        setDraggingStudyId(null);
    }, []);
    // The card a row expands into; shared by the table (a full-width <tr>) and
    // the phone card list (a block under the card).
    const renderExpandedContent = (study, display) => (_jsx(ExpandedRowContent, { study: study, display: display, className: isSm ? undefined : 'mx-0', openConsultationOnMount: consultationToOpen === study.id, onConsultationOpened: () => setConsultationToOpen(null), onView: () => openViewer(study.id), onOpenRelatedStudy: (relatedStudyId) => 
        // Remember the hosting card, not the related study — the return trip
        // should land on the card the user launched from.
        openViewer(relatedStudyId, undefined, study.id), onThumbnailOpen: (seriesId) => openViewer(study.id, seriesId), onDelete: () => {
            trackStudyAction('study.deleted');
            setDeleteModalStudyId(study.id);
        }, onShare: () => {
            trackStudyAction('study.shared');
            setShareDialogStudyId(study.id);
        }, onEmail: () => {
            trackStudyAction('study.emailed');
            setEmailDialogStudyId(study.id);
        }, onDownload: () => {
            trackStudyAction('study.downloaded');
            setDownloadStudyIds([study.id]);
        }, onDicomSend: () => {
            trackStudyAction('study.dicom_sent');
            setDicomSendStudyId(study.id);
        }, onMerge: () => setMergeDialogStudyId(study.id), onAnonymize: () => setAnonymizeStudyId(study.id), onPrefetch: () => prefetchStudy(study.id) }));
    // The stethoscope glyph: same warm-up as expanding by hand; the modal then
    // sits over the open card.
    const openConsultationFor = (study) => study.consultationCaseId
        ? () => {
            setExpandedStudies(new Set([study.id]));
            prefetchStudy(study.id);
            studyWarmup.warm(study.id);
            setConsultationToOpen(study.id);
        }
        : undefined;
    if (isLoading) {
        return (_jsx("div", { className: "space-y-2", children: Array.from({ length: 10 }).map((_, i) => (_jsx(Skeleton, { className: "h-12 w-full" }, `skeleton-${i}`))) }));
    }
    const emptyState = hasNoClinic ? (
    // A membership-less account (TC-only radiologist/admin) can never have
    // studies here; point at the surface that has their work instead of a
    // misleading "No studies found".
    _jsxs("div", { className: "mx-auto flex max-w-md flex-col items-center gap-2", "data-testid": "worklist-no-clinic", children: [_jsx("p", { className: "text-foreground text-sm font-medium", children: t('worklist.noClinicTitle') }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('worklist.noClinicBody') }), _jsxs(Button, { size: "sm", className: "mt-2", onClick: () => setLocation('/teleconsultation'), "data-testid": "worklist-no-clinic-action", children: [_jsx(MessageSquare, { className: "mr-2 h-4 w-4" }), t('worklist.noClinicAction')] })] })) : (t('worklist.noStudiesFound'));
    return (_jsxs("div", { className: "flex flex-col", children: [!isSm ? (_jsx(WorklistCardList, { studies: studies, displayFor: displayFor, expandedIds: expandedStudies, receivingIds: receivingStudyIds, emptyState: emptyState, onRowClick: handleRowClick, onToggle: toggleExpand, onOpenViewer: (studyId) => openViewer(studyId), openConsultationFor: openConsultationFor, renderExpanded: renderExpandedContent })) : (
            // From lg the table fits its column (worklist columns are tiered by
            // width), so both wrappers stop being scroll containers and `main` (the
            // page's vertical scroller) is the one the sticky header sticks to.
            // Below lg the wrapper keeps scrolling sideways for narrow tablets.
            // -top-2 cancels main's pt-2: sticky offsets are measured inside the
            // scroller's padding, so at top-0 the rows scrolled through an 8px
            // band above the stuck header.
            _jsx("div", { className: "overflow-auto lg:overflow-visible lg:[&>div]:overflow-visible", children: _jsxs(Table, { className: "w-full table-fixed", children: [_jsx(TableHeader, { className: "lg:bg-background lg:sticky lg:-top-2 lg:z-10", children: _jsxs(TableRow, { className: "border-b border-[#1E293B] hover:bg-transparent", style: { background: 'transparent' }, children: [_jsx(TableHead, { style: { width: EXPANDER_COLUMN_WIDTH } }), visibleColumns.map((col) => (_jsx(WorklistHeaderCell, { columnKey: col.key, onSort: handleSort }, col.key))), showNotifyColumn && (_jsx(TableHead, { className: "text-muted-foreground/70 text-center text-[9px] font-semibold tracking-wider uppercase", style: { width: NOTIFY_COLUMN_WIDTH }, children: t('worklist.col.notify') }))] }) }), _jsx(TableBody, { children: studies.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: columnCount, className: "text-muted-foreground py-8 text-center", children: emptyState }) })) : (studies.map((study) => {
                                const isExpanded = expandedStudies.has(study.id);
                                const display = displayFor(study);
                                const detailsId = `study-details-${study.id}`;
                                return (_jsxs(Fragment, { children: [_jsx(WorklistTableRow, { study: study, display: display, columns: visibleColumns.map((c) => c.key), showNotifyColumn: showNotifyColumn, isExpanded: isExpanded, isReceiving: receivingStudyIds.has(study.id), isDragging: draggingStudyId === study.id, detailsId: detailsId, onRowClick: (e) => handleRowClick(e, study.id), onToggle: () => toggleExpand(study.id), onHover: () => handleRowHover(study.id), onHoverEnd: handleRowHoverEnd, onDragStart: (e) => handleDragStart(e, study.id), onDragEnd: handleDragEnd, onOpenViewer: () => openViewer(study.id), onOpenConsultation: openConsultationFor(study), onPrefetch: () => prefetchStudy(study.id) }), isExpanded && (_jsx("tr", { id: detailsId, "data-testid": detailsId, children: _jsx("td", { colSpan: columnCount, className: "border-border max-w-0 border-b p-0", children: renderExpandedContent(study, display) }) }))] }, study.id));
                            })) })] }) })), _jsxs("div", { className: "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-[#1E293B] bg-[#0B0F14] px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]", children: [_jsxs("div", { className: "flex min-w-0 items-center gap-3", children: [isSm && (_jsx(WorklistColumnsMenu, { className: "-ml-2", columns: customColumns, visibleCount: preferredVisibleColumns.length, onToggle: setCustomColumnVisibility, onMove: moveCustomColumn, onReset: resetColumnPrefs })), _jsx("span", { className: "text-muted-foreground/60 font-mono text-[11px] tracking-wider uppercase", children: t('worklist.showing', {
                                    from: studies.length > 0 ? (page - 1) * pageSize + 1 : 0,
                                    to: Math.min(page * pageSize, total),
                                    total,
                                }) })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => handlePageChange(page - 1), disabled: page <= 1, className: "text-muted-foreground relative h-7 px-2 text-xs before:absolute before:-inset-x-1 before:-inset-y-2.5 pointer-coarse:h-10 pointer-coarse:px-3", children: t('worklist.previous') }), _jsxs("span", { className: "text-muted-foreground rounded bg-[#161B22] px-2 py-0.5 font-mono text-[11px]", children: [t('worklist.page'), " ", _jsx("span", { className: "text-foreground", children: page }), " ", t('worklist.of'), ' ', totalPages || 1] }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => handlePageChange(page + 1), disabled: page >= totalPages, className: "text-muted-foreground relative h-7 px-2 text-xs before:absolute before:-inset-x-1 before:-inset-y-2.5 pointer-coarse:h-10 pointer-coarse:px-3", children: t('worklist.next') })] })] }), shareDialogStudyId && (_jsx(ShareDialog, { studyId: shareDialogStudyId, open: !!shareDialogStudyId, onOpenChange: (open) => !open && setShareDialogStudyId(null) })), emailDialogStudyId &&
                (() => {
                    const studyToEmail = studies.find((s) => s.id === emailDialogStudyId);
                    if (!studyToEmail)
                        return null;
                    return (_jsx(EmailStudyDialog, { study: studyToEmail, open: !!emailDialogStudyId, onOpenChange: (open) => !open && setEmailDialogStudyId(null) }));
                })(), downloadStudyIds && downloadStudyIds.length > 0 && (_jsx(DownloadStudyDialog, { studyIds: downloadStudyIds, open: !!downloadStudyIds, onOpenChange: (open) => !open && setDownloadStudyIds(null) })), dicomSendStudyId &&
                (() => {
                    const studyToSend = studies.find((s) => s.id === dicomSendStudyId);
                    if (!studyToSend)
                        return null;
                    return (_jsx(DicomSendDialog, { study: studyToSend, open: !!dicomSendStudyId, onOpenChange: (open) => !open && setDicomSendStudyId(null) }));
                })(), mergeDialogStudyId &&
                (() => {
                    const studyToMerge = studies.find((s) => s.id === mergeDialogStudyId);
                    if (!studyToMerge)
                        return null;
                    return (_jsx(MergeStudiesDialog, { study: studyToMerge, open: !!mergeDialogStudyId, onOpenChange: (open) => !open && setMergeDialogStudyId(null) }));
                })(), anonymizeStudyId &&
                (() => {
                    const studyToAnonymize = studies.find((s) => s.id === anonymizeStudyId);
                    if (!studyToAnonymize)
                        return null;
                    return (_jsx(AnonymizeStudyDialog, { study: studyToAnonymize, open: !!anonymizeStudyId, onOpenChange: (open) => !open && setAnonymizeStudyId(null), isLoading: anonymizeMutation.isPending, onSubmit: (body) => {
                            anonymizeMutation.mutate({ studyId: studyToAnonymize.id, body }, {
                                onSuccess: (result) => {
                                    trackStudyAction('study.anonymized');
                                    setAnonymizeStudyId(null);
                                    setAnonymizeJobIds((ids) => [...ids, result.jobId]);
                                    toast({
                                        title: t('anonymize.started'),
                                        description: t('anonymize.startedDesc'),
                                    });
                                },
                                onError: (error) => {
                                    toast({
                                        title: t('anonymize.requestFailed'),
                                        description: error instanceof Error ? error.message : undefined,
                                        variant: 'destructive',
                                    });
                                },
                            });
                        } }));
                })(), anonymizeJobIds.map((jobId) => (_jsx(AnonymizeJobWatcher, { jobId: jobId, onDone: (status) => {
                    // The copy's image count jumps from 0 to N in one poll, which reads
                    // as an arrival and would keep the "Receiving" chip up for the whole
                    // grace window; the job's completion is better evidence than counts.
                    if (status.state === 'completed' && status.newStudyId)
                        settleReceiving(status.newStudyId);
                    setAnonymizeJobIds((ids) => ids.filter((id) => id !== jobId));
                } }, jobId))), deleteModalStudyId &&
                (() => {
                    const studyToDelete = studies.find((s) => s.id === deleteModalStudyId);
                    return (_jsx(DeleteModal, { open: !!deleteModalStudyId, onOpenChange: (open) => !open && setDeleteModalStudyId(null), studyId: deleteModalStudyId, initialSeriesCount: studyToDelete?.counts.seriesCount ?? undefined, initialImageCount: studyToDelete?.counts.imageCount ?? undefined, modality: studyToDelete?.study.modality, hasCineloopImages: studyToDelete?.counts.hasCineloopImages, onDeleted: () => setDeleteModalStudyId(null) }));
                })()] }));
}
